import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
type FaqItem = {
  question: string;
  answer: string;
};
const faqs: FaqItem[] = [{
  question: 'What does a maintenance program at Lancer actually include?',
  answer: "It starts with understanding where your vehicle stands — we pull your history, check it against your manufacturer's recommended intervals, and identify what's due, what's coming up, and what's fine. Every visit includes a 47-point Digital Vehicle Inspection with photos and video sent to your phone. From there, we work through your vehicle's schedule with you — oil service, fluid flushes, filters, brake service, suspension, and everything else that keeps a car running well long-term."
}, {
  question: "Will you recommend things I don't actually need?",
  answer: "No. If something can wait, we'll tell you it can wait. If your car looks good, you'll hear that. On a recent visit, we pulled a customer's Carfax and told them several services they came in expecting weren't actually due yet. That's how we work — recommendations based on what we actually find."
}, {
  question: 'How is this different from just getting an oil change?',
  answer: "An oil change is one item on a much longer list. It keeps your engine lubricated — it doesn't flush your brake fluid, replace your cabin filter, inspect your suspension, or tell you your timing belt is due. A maintenance program covers all of it, on the right schedule, before things become problems. Oil changes are part of a maintenance program. They're not the whole thing."
}, {
  question: 'How do I know what maintenance my car actually needs?',
  answer: "You probably don't. That's what the inspection is for. We pull your vehicle history, check manufacture intervals, compare those with the real time results of the inspection, and tell you exactly where things stand. Most manufacturers will set maintenance intervals at certain mileages to keep the advertised cost of ownership down. The term lifetime is used a lot in regards to maintenance and unfortunately there is no such thing. A lifetime for a manufacturer is 100,000 miles. At that point they would be looking to sell a new car. In today's day in age vehicles will go well beyond 100k but only with proper care. We want your vehicle to last as long as you want to keep it. To do this we believe in being proactive with maintenance and when needed performing before the manufacturer interval. This will set a strong foundation to get the most out of your vehicle once the mileage starts to add up. No guess work, no pressure, and no recommendations that aren't backed by what we actually find."
}, {
  question: 'Do you work on hybrids and EVs?',
  answer: 'Yes. We service all makes and models, including Toyota hybrids, plug-in hybrids, and EVs. Maintenance needs for hybrids differ in important ways from conventional vehicles — hybrid battery inspection and service is something many independent shops refer out. We handle it in-house.'
}, {
  question: 'What does a 30K / 60K / 90K service include?',
  answer: "It depends on your vehicle's make, model, and actual condition — not just the mileage number. We follow manufacturer-recommended intervals and adjust based on what we find during the inspection. You'll know exactly what was done and why before we do anything."
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="bg-brand-light px-4 py-20" data-id="element-22" data-mp="qfy8az41cz3n">
      <div className="mx-auto max-w-4xl" data-id="element-23" data-mp="qg7yamp2z69c">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="mb-16 text-center" data-id="element-24" data-mp="vet4d2r0kk5p">
          <span className="mb-4 block text-sm font-bold uppercase tracking-widest text-brand-accent" data-id="element-25" data-mp="21xsscmj3wnzy">
            COMMON QUESTIONS
          </span>
          <h2 className="font-heading text-3xl font-extrabold uppercase tracking-wide text-brand-dark md:text-4xl lg:text-5xl" data-id="element-26" data-mp="2buf9b08bauwp">
            THINGS PEOPLE ASK BEFORE THEIR FIRST VISIT
          </h2>
        </motion.div>

        <div className="space-y-4" data-id="element-27" data-mp="1j69nsb9fp1vt">
          {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return <motion.div key={faq.question} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm" data-id="element-28" data-mp="1svb5c5vmnrbw" data-mpi={index}>
                <button type="button" onClick={() => setOpenIndex(isOpen ? null : index)} className="flex w-full items-center justify-between gap-4 p-6 text-left transition-colors hover:bg-gray-50 md:p-7" aria-expanded={isOpen} data-id="element-29" data-mp="3bw2b2djdxndg">
                  <span className="font-heading text-base font-bold uppercase tracking-wide text-brand-dark md:text-lg" data-id="element-30" data-mp="3ustnc30if280">
                    {faq.question}
                  </span>
                  <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors ${isOpen ? 'bg-brand-accent text-white' : 'bg-brand-light text-brand-dark'}`} data-id="element-31" data-mp="3gv58bbt4ipav">
                    {isOpen ? <MinusIcon className="h-5 w-5" data-id="element-32" data-mp="29h206bbysemw" /> : <PlusIcon className="h-5 w-5" data-id="element-33" data-mp="fvqdmpsznbl8" />}
                  </span>
                </button>
                <AnimatePresence initial={false} data-id="element-34" data-mp="aygrbj0wbg1m">
                  {isOpen && <motion.div key="content" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-35" data-mp="65nkmplv9xmw">
                      <p className="px-6 pb-6 text-lg leading-relaxed text-gray-700 md:px-7 md:pb-7" data-id="element-36" data-mp="1cmsmtjw9jqwx">
                        {faq.answer}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}